/*
 * AU NOIR custom.js — V7 PERFORMANCE OPTIMIZED / STRICT QUICK VIEW ISOLATION
 * Generated from the 2026-09-21 custom.js supplied in chat.
 *
 * Main optimizations:
 * - Removed duplicate mobile filter module.
 * - Removed initial Quick Size product fetch for every card.
 * - Removed Quick Size full-document MutationObserver.
 * - Quick View now prefetches only the color the user approaches.
 * - Collection-only network patches now install only on collections.
 * - Dynamic styling observers process only added nodes instead of re-scanning the whole page.
 * - Existing customer-facing behavior is otherwise preserved.
 */

/*
=====================================================================
STRETCH + SA VARIANTS — SAFE COLLECTION SYNC
=====================================================================

This version NEVER:
- preventDefault()
- stopPropagation()
- stopImmediatePropagation()
- button.click()
- replace quick-buy-modal

So Stretch's "Select options" button keeps its native click behavior.

What it DOES:
1) Detect the child product selected by SA Variants.
2) Update the EXISTING Stretch quick-buy-modal product-url immediately.
3) Clear any previously-loaded quick-buy HTML so Stretch reloads the
   newly selected child product on the next normal button click.
4) Reset the mobile-only image carousel to slide 1.

Load AFTER theme.js.
Remove ALL previous quick-buy / SA Variants custom JS before using this.
=====================================================================
*/

(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 699px)';
  const CARD_SELECTOR = 'product-card';

  const selectedUrlByCard = new WeakMap();
  const originalUrlByCard = new WeakMap();

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;


  /* ================================================================
     URL HELPERS
     ================================================================ */

  function cleanProductUrl(value) {
    if (!value || typeof value !== 'string') return '';

    try {
      const url = new URL(value, window.location.origin);

      if (!url.pathname.includes('/products/')) {
        return '';
      }

      /*
        Remove collection context:
        /collections/foo/products/bar -> /products/bar
      */
      const productIndex = url.pathname.indexOf('/products/');

      if (productIndex >= 0) {
        url.pathname = url.pathname.slice(productIndex);
      }

      return url.pathname + url.search;
    } catch (_) {
      const match = value.match(/\/(?:collections\/[^/?#]+\/)?products\/[^"'?#\s]+(?:\?[^"'\s#]*)?/i);

      if (!match) return '';

      return match[0].replace(/^\/collections\/[^/]+/, '');
    }
  }


  function handleFromValue(value) {
    if (!value || typeof value !== 'string') return '';

    /*
      Some apps use data-product-handle / data-handle rather than URLs.
      Ignore values that clearly aren't handles.
    */
    if (
      value.includes(' ') ||
      value.includes('/') ||
      value.includes('?') ||
      value.includes('#')
    ) {
      return '';
    }

    if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(value)) {
      return '';
    }

    return value;
  }


  /* ================================================================
     GET PRODUCT URL FROM SA VARIANTS ELEMENT
     ================================================================ */

  function productUrlFromElement(startNode, card) {
    if (!startNode || !card) return '';

    let node = startNode.nodeType === 1
      ? startNode
      : startNode.parentElement;

    while (node && node !== card.parentElement) {
      /*
        Normal linked swatch.
      */
      if (node.matches?.('a[href]')) {
        const href = cleanProductUrl(
          node.getAttribute('href') || node.href || ''
        );

        if (href) return href;
      }

      if (node.attributes) {
        for (const attr of node.attributes) {
          const name = attr.name.toLowerCase();
          const value = attr.value || '';

          /*
            Any attribute containing a Shopify product URL.
          */
          const url = cleanProductUrl(value);

          if (url) return url;

          /*
            SA / StarApps may expose just a product handle.
          */
          if (
            name.includes('product-handle') ||
            name === 'data-handle' ||
            name.endsWith('-handle')
          ) {
            const handle = handleFromValue(value);

            if (handle) {
              return `/products/${handle}`;
            }
          }
        }
      }

      /*
        Sometimes the actual product <a> is nested inside the swatch.
      */
      const nested = node.querySelector?.('a[href*="/products/"]');

      if (nested) {
        const href = cleanProductUrl(
          nested.getAttribute('href') || nested.href || ''
        );

        if (href) return href;
      }

      if (node === card) break;

      node = node.parentElement;
    }

    return '';
  }


  /* ================================================================
     READ CURRENT PRODUCT URL FROM STRETCH CARD
     ================================================================ */

  function currentCardUrl(card) {
    if (!card) return '';

    const selectors = [
      '.product-title[href*="/products/"]',
      '.product-card__media[href*="/products/"]',
      'a[href*="/products/"]'
    ];

    for (const selector of selectors) {
      const link = card.querySelector(selector);

      if (!link) continue;

      const url = cleanProductUrl(
        link.getAttribute('href') || link.href || ''
      );

      if (url) return url;
    }

    const handle = card.getAttribute('handle');

    return handle ? `/products/${handle}` : '';
  }


  function rememberOriginal(card) {
    if (!card || originalUrlByCard.has(card)) return;

    const url = currentCardUrl(card);

    if (url) {
      originalUrlByCard.set(card, url);
    }
  }


  /* ================================================================
     UPDATE STRETCH QUICK BUY WITHOUT TOUCHING THE BUTTON
     ================================================================ */

  function syncQuickBuyModal(card, productUrl) {
    if (!card) return;

    const clean = cleanProductUrl(productUrl);

    if (!clean) return;

    selectedUrlByCard.set(card, clean);
    card.dataset.saSelectedProductUrl = clean;

    const quickBuyButton = card.querySelector(
      '.product-card__quick-buy [aria-controls]'
    );

    if (!quickBuyButton) return;

    const modalId = quickBuyButton.getAttribute('aria-controls');

    if (!modalId) return;

    const modal = document.getElementById(modalId);

    if (
      !modal ||
      modal.tagName.toLowerCase() !== 'quick-buy-modal'
    ) {
      return;
    }

    /*
      CRITICAL MODAL ISOLATION:
      Once Quick View is open, this COLLECTION-CARD synchronizer must not
      rewrite product-url or clear the modal. The open Quick View has its own
      switching controller below. Without this guard, mutations created by a
      Quick View product swap can wake this collection observer and restore
      the card's previous product into the modal.
    */
    if (modal.hasAttribute('open')) {
      return;
    }

    const current = cleanProductUrl(
      modal.getAttribute('product-url') || ''
    );

    if (current !== clean) {
      /*
        KEY DIFFERENCE FROM THE OLD CODE:
        Keep the SAME custom element in the DOM.
        Only change the URL Stretch will load.
      */
      modal.setAttribute('product-url', clean);

      /*
        Stretch fills this modal dynamically.
        Clearing stale content forces the next NORMAL Select options click
        to load the newly selected SA child product.
      */
      if (modal.innerHTML.trim() !== '') {
        modal.innerHTML = '';
      }

      /*
        Remove common "already loaded" markers if present.
        These are harmless if Stretch doesn't use them.
      */
      modal.removeAttribute('loaded');
      modal.removeAttribute('data-loaded');
      modal.removeAttribute('data-product-loaded');
    }
  }


  /* ================================================================
     MOBILE CAROUSEL
     ================================================================ */

  function resetMobileGallery(card) {
    if (!isMobile() || !card) return;

    const gallery = card.querySelector(
      '.product-card__media--gallery'
    );

    if (!gallery) return;

    requestAnimationFrame(() => {
      try {
        gallery.scrollTo({
          left: 0,
          behavior: 'auto'
        });
      } catch (_) {
        gallery.scrollLeft = 0;
      }
    });
  }


  /* ================================================================
     DETECT SA VARIANTS INTERACTION
     ================================================================ */

  function looksLikeVariantInteraction(target, card) {
    if (!target || !card) return false;

    /*
      IMPORTANT:
      Collection-card syncing must NEVER react to swatches inside an open
      Quick View. Quick View has its own isolated switching system below.
      Without this guard, the collection-card module can rewrite/clear the
      modal while the Quick View module is switching products.
    */
    if (target.closest?.('quick-buy-modal')) {
      return false;
    }

    /*
      Never treat Stretch's own Quick Buy or product links as SA swatches.
    */
    if (
      target.closest('.product-card__quick-buy') ||
      target.closest('.product-card__media') ||
      target.closest('.product-title')
    ) {
      return false;
    }

    let node = target.nodeType === 1
      ? target
      : target.parentElement;

    while (node && node !== card.parentElement) {
      const signature = [
        node.id || '',
        typeof node.className === 'string'
          ? node.className
          : '',
        ...Array.from(node.attributes || []).map(
          attr => `${attr.name}=${attr.value}`
        )
      ].join(' ').toLowerCase();

      if (
        signature.includes('variant') ||
        signature.includes('swatch') ||
        signature.includes('starapp') ||
        signature.includes('sa-variant') ||
        signature.includes('product-group')
      ) {
        return true;
      }

      if (node === card) break;

      node = node.parentElement;
    }

    return Boolean(productUrlFromElement(target, card));
  }


  function processVariantInteraction(target) {
    const card = target?.closest?.(CARD_SELECTOR);

    if (!card) return;
    if (!looksLikeVariantInteraction(target, card)) return;

    rememberOriginal(card);

    /*
      Best case:
      the SA Variants swatch directly tells us the child product URL.
    */
    const directUrl = productUrlFromElement(target, card);

    if (directUrl) {
      syncQuickBuyModal(card, directUrl);
    }

    /*
      SA Variants may update Stretch's title/media link asynchronously.
      Re-check a few times AFTER the app has processed the swatch.

      If we already have a direct child-product URL, don't overwrite it
      with the original card URL.
    */
    [30, 100, 250, 500].forEach(delay => {
      setTimeout(() => {
        const current = currentCardUrl(card);
        const original = originalUrlByCard.get(card) || '';
        const selected =
          selectedUrlByCard.get(card) ||
          card.dataset.saSelectedProductUrl ||
          '';

        if (
          current &&
          (
            !selected ||
            current !== original
          )
        ) {
          syncQuickBuyModal(card, current);
        }

        resetMobileGallery(card);
      }, delay);
    });
  }


  /* ================================================================
     EVENTS — NOTICE THERE IS NO QUICK-BUY BUTTON HANDLER
     ================================================================ */

  document.addEventListener(
    'pointerdown',
    event => {
      const card = event.target.closest?.(CARD_SELECTOR);

      if (!card) return;

      if (looksLikeVariantInteraction(event.target, card)) {
        rememberOriginal(card);

        const directUrl =
          productUrlFromElement(event.target, card);

        if (directUrl) {
          syncQuickBuyModal(card, directUrl);
        }
      }
    },
    true
  );


  document.addEventListener(
    'click',
    event => {
      processVariantInteraction(event.target);
    },
    true
  );


  document.addEventListener(
    'change',
    event => {
      processVariantInteraction(event.target);
    },
    true
  );


  /* ================================================================
     WATCH CHANGES MADE BY SA VARIANTS
     ================================================================ */

  const observer = new MutationObserver(mutations => {
    const cards = new Set();

    for (const mutation of mutations) {
      const element = mutation.target.nodeType === 1
        ? mutation.target
        : mutation.target.parentElement;

      /*
        Never let collection synchronization react to DOM work happening
        inside Quick View. A quick-buy-modal is often nested under its
        product-card, so closest('product-card') alone is not enough.
      */
      if (element?.closest?.('quick-buy-modal')) {
        continue;
      }

      const card = element?.closest?.(CARD_SELECTOR);

      if (card) cards.add(card);
    }

    cards.forEach(card => {
      /*
        While this card's Quick View is open, the modal owns product state.
        Do not push collection-card state back into it.
      */
      if (card.querySelector?.('quick-buy-modal[open]')) {
        return;
      }

      rememberOriginal(card);

      const current = currentCardUrl(card);
      const original = originalUrlByCard.get(card) || '';

      if (current && current !== original) {
        syncQuickBuyModal(card, current);
        resetMobileGallery(card);
      }
    });
  });


  function startObserver() {
    const root =
      document.querySelector('main') ||
      document.body;

    if (!root) return;

    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        'href',
        'handle',
        'data-url',
        'data-product-url',
        'data-product-handle',
        'data-handle'
      ]
    });
  }


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      { once: true }
    );
  } else {
    startObserver();
  }

})();

/* ============================================================
   AU NOIR — SV VARIANTS COLLECTION
   PRICE + SALE BADGE SYNC

   SAFE VERSION
   - No MutationObserver
   - No preventDefault
   - No click simulation
   - No Quick Buy interference
   ============================================================ */

(() => {
  'use strict';

  const SWATCH_SELECTOR =
    '.swatch-group-selector[swatch-url]';

  const CACHE = new Map();

  const CURRENT_PRODUCT =
    new WeakMap();


  /* ============================================================
     CLEAN PRODUCT URL
     ============================================================ */

  function cleanUrl(value) {

    if (!value) {
      return '';
    }


    try {

      const url =
        new URL(
          value,
          window.location.origin
        );


      const productIndex =
        url.pathname.indexOf(
          '/products/'
        );


      if (productIndex === -1) {
        return '';
      }


      /*
        Remove collection context.

        /collections/shirts/products/foo
        becomes
        /products/foo
      */

      url.pathname =
        url.pathname.slice(
          productIndex
        );


      /*
        Keep variant ID if SV supplied one.
      */

      const variant =
        url.searchParams.get(
          'variant'
        );


      url.search = '';


      if (variant) {

        url.searchParams.set(
          'variant',
          variant
        );

      }


      return (
        url.pathname +
        url.search
      );


    } catch (_) {

      return '';

    }

  }



  /* ============================================================
     MONEY FROM BADGE
     ============================================================ */

  function getBadgeHtmlFromDocument(
    doc
  ) {

    /*
      Look for Stretch's real sale badge
      anywhere on fetched product HTML.
    */

    const badge =
      doc.querySelector(
        'on-sale-badge'
      );


    if (!badge) {
      return '';
    }


    return badge.innerHTML;

  }



  /* ============================================================
     PRICE HTML CLEANUP

     Stretch / translated price markup can sometimes output:
     <span class="sr-only">Selling price</span>: €94.95

     The colon is a real text node after the accessibility label.
     Remove ONLY that leading colon. Never touch the price digits.
     ============================================================ */

  function cleanPriceHtml(
    priceList
  ) {

    if (!priceList) {
      return '';
    }


    const clone =
      priceList.cloneNode(true);


    clone
      .querySelectorAll(
        'sale-price, compare-at-price'
      )
      .forEach(priceElement => {

        const srOnly =
          priceElement.querySelector(
            ':scope > .sr-only'
          );


        if (!srOnly) {
          return;
        }


        let node =
          srOnly.nextSibling;


        while (
          node &&
          node.nodeType === Node.TEXT_NODE &&
          node.textContent.trim() === ''
        ) {
          node = node.nextSibling;
        }


        if (
          node &&
          node.nodeType === Node.TEXT_NODE
        ) {

          node.textContent =
            node.textContent.replace(
              /^\s*:\s*/,
              ''
            );

        }

      });


    return clone.innerHTML;

  }



  /* ============================================================
     GET PRICE FROM STRETCH HTML
     ============================================================ */

  function getPriceHtmlFromDocument(
    doc
  ) {

    /*
      First try Stretch Quick Buy.
    */

    const template =
      doc.getElementById(
        'quick-buy-content'
      );


    if (
      template &&
      template.content
    ) {

      const price =
        template.content.querySelector(
          'price-list'
        );


      if (price) {

        return cleanPriceHtml(
          price
        );

      }

    }


    /*
      Product page fallback.
    */

    const productPrice =
      doc.querySelector(
        '.product-info__block-item[data-block-type="price"] price-list'
      ) ||

      doc.querySelector(
        '.product-info price-list'
      );


    if (productPrice) {

      return cleanPriceHtml(
        productPrice
      );

    }


    return '';

  }



  /* ============================================================
     DETECT SALE USING PRICE MARKUP
     ============================================================ */

  function priceIsOnSale(
    priceHtml
  ) {

    if (!priceHtml) {
      return false;
    }


    const holder =
      document.createElement(
        'div'
      );


    holder.innerHTML =
      `<price-list>${priceHtml}</price-list>`;


    /*
      Stretch normally outputs compare-at-price
      when product is discounted.
    */

    return Boolean(

      holder.querySelector(
        'compare-at-price'
      ) ||

      holder.querySelector(
        'del'
      ) ||

      holder.querySelector(
        '.price-list--on-sale'
      )

    );

  }



  /* ============================================================
     GET SAVINGS AMOUNT FROM PRICE HTML
     ============================================================ */

  function getSavingsText(
    priceHtml
  ) {

    if (!priceHtml) {
      return '';
    }


    const holder =
      document.createElement(
        'div'
      );


    holder.innerHTML =
      `<price-list>${priceHtml}</price-list>`;


    const saleElement =
      holder.querySelector(
        'sale-price .money'
      );


    const compareElement =
      holder.querySelector(
        'compare-at-price .money'
      );


    if (
      !saleElement ||
      !compareElement
    ) {
      return '';
    }


    const parseMoney =
      text => {

        if (!text) {
          return NaN;
        }


        let cleaned =
          text
            .replace(
              /[^0-9,.\-]/g,
              ''
            );


        /*
          Canadian/US formatting:
          1,299.00
        */

        if (
          cleaned.includes(',') &&
          cleaned.includes('.')
        ) {

          cleaned =
            cleaned.replace(
              /,/g,
              ''
            );

        }

        /*
          Handle comma decimal if needed.
        */

        else if (
          cleaned.includes(',') &&
          !cleaned.includes('.')
        ) {

          const pieces =
            cleaned.split(',');


          if (
            pieces[
              pieces.length - 1
            ].length === 2
          ) {

            cleaned =
              cleaned.replace(
                ',',
                '.'
              );

          } else {

            cleaned =
              cleaned.replace(
                /,/g,
                ''
              );

          }

        }


        return parseFloat(
          cleaned
        );

      };


    const sale =
      parseMoney(
        saleElement.textContent
      );


    const compare =
      parseMoney(
        compareElement.textContent
      );


    if (
      !Number.isFinite(sale) ||
      !Number.isFinite(compare) ||
      compare <= sale
    ) {

      return '';

    }


    const savings =
      compare - sale;


    /*
      Keep whole dollar when possible.
    */

    const formatted =
      Number.isInteger(savings)

        ? savings.toFixed(0)

        : savings.toFixed(2);


    /*
      Detect currency symbol from
      Stretch's rendered money.
    */

    const saleText =
      saleElement.textContent
        .trim();


    let symbol = '$';


    if (
      saleText.includes('€')
    ) {

      symbol = '€';

    }

    else if (
      saleText.includes('£')
    ) {

      symbol = '£';

    }


    return (
      symbol +
      formatted
    );

  }



  /* ============================================================
     FETCH SELECTED SV PRODUCT
     ============================================================ */

  async function getProductData(
    productUrl
  ) {

    const url =
      cleanUrl(
        productUrl
      );


    if (!url) {
      return null;
    }


    if (
      CACHE.has(url)
    ) {

      return CACHE.get(
        url
      );

    }


    const request =
      fetch(
        url,
        {
          credentials:
            'same-origin'
        }
      )

        .then(
          response => {

            if (
              !response.ok
            ) {

              throw new Error(
                'Product request failed: ' +
                response.status
              );

            }


            return response.text();

          }
        )


        .then(
          html => {

            const doc =
              new DOMParser()
                .parseFromString(
                  html,
                  'text/html'
                );


            const priceHtml =
              getPriceHtmlFromDocument(
                doc
              );


            let badgeHtml =
              getBadgeHtmlFromDocument(
                doc
              );


            const onSale =
              priceIsOnSale(
                priceHtml
              );


            /*
              If Stretch's fetched product HTML
              does not contain its sale badge,
              build the same badge contents from
              the rendered price.
            */

            if (
              onSale &&
              !badgeHtml
            ) {

              const savings =
                getSavingsText(
                  priceHtml
                );


              if (savings) {

                badgeHtml =
                  `Save <span class="money">${savings}</span>`;

              }

            }


            return {

              url,

              priceHtml,

              badgeHtml,

              onSale

            };

          }
        )


        .catch(
          error => {

            CACHE.delete(
              url
            );


            console.warn(
              '[SV Collection Price + Badge]',
              error
            );


            return null;

          }
        );


    CACHE.set(
      url,
      request
    );


    return request;

  }



  


  /* ============================================================
     UPDATE SALE BADGE

     The original file called updateSaleBadge() but did not define it.
     Stretch/Maestrooo product cards use .product-card__badge-list
     with <on-sale-badge class="badge badge--on-sale">.
     ============================================================ */

  function updateSaleBadge(card, data) {
    if (!card || !data) return;

    const badgeList = card.querySelector(
      '.product-card__badge-list'
    );

    if (!badgeList) return;

    let saleBadge = badgeList.querySelector(
      'on-sale-badge'
    );

    if (!data.onSale) {
      saleBadge?.remove();
      return;
    }

    if (!data.badgeHtml) return;

    if (!saleBadge) {
      saleBadge = document.createElement(
        'on-sale-badge'
      );

      saleBadge.className =
        'badge badge--on-sale';

      badgeList.appendChild(saleBadge);
    }

    saleBadge.hidden = false;
    saleBadge.innerHTML = data.badgeHtml;
  }


  /* ============================================================
     UPDATE COLLECTION CARD
     ============================================================ */

  async function updateCard(
    card,
    productUrl
  ) {

    if (
      !card ||
      !productUrl
    ) {
      return;
    }


    const url =
      cleanUrl(
        productUrl
      );


    if (!url) {
      return;
    }


    /*
      Avoid duplicate requests from
      pointerdown + click.
    */

    if (
      CURRENT_PRODUCT.get(
        card
      ) === url
    ) {

      return;

    }


    CURRENT_PRODUCT.set(
      card,
      url
    );


    const data =
      await getProductData(
        url
      );


    if (!data) {
      return;
    }


    /*
      User may have selected another
      SV color while fetch was running.
    */

    if (
      CURRENT_PRODUCT.get(
        card
      ) !== url
    ) {

      return;

    }



    /* ========================================================
       UPDATE PRICE
       ======================================================== */

    const priceList =
      card.querySelector(
        '.product-card__info price-list'
      );


    if (
      priceList &&
      data.priceHtml
    ) {

      priceList.innerHTML =
        data.priceHtml;

    }



    /* ========================================================
       UPDATE SALE BADGE
       ======================================================== */

    updateSaleBadge(
      card,
      data
    );

  }



  /* ============================================================
     SV CLICK
     ============================================================ */

  document.addEventListener(
    'click',

    event => {

      const swatch =
        event.target.closest?.(
          SWATCH_SELECTOR
        );


      if (!swatch) {
        return;
      }


      /*
        Collection cards only.
        Quick View has its own existing code.
      */

      const card =
        swatch.closest(
          'product-card'
        );


      if (!card) {
        return;
      }


      /*
        Do not touch swatches inside
        Stretch Quick View.
      */

      if (
        swatch.closest(
          'quick-buy-modal'
        )
      ) {

        return;

      }


      const productUrl =
        swatch.getAttribute(
          'swatch-url'
        );


      if (!productUrl) {
        return;
      }


      updateCard(
        card,
        productUrl
      );

    },

    true
  );

})();

/* ============================================================
   AU NOIR — COLLECTION LOAD MORE V1

   Replaces automatic infinite scroll with a controlled Load More button.

   PERFORMANCE GOALS
   - No IntersectionObserver continuously preloading pages.
   - No previous-page loading.
   - No scroll URL tracking.
   - Next page is fetched ONLY when the customer clicks.
   - Entire next batch is appended in ONE DOM mutation.
   - Swatch King is explicitly asked to render dynamic swatches once.
   - Clean AU NOIR / BOSS-style progress indicator.
   ============================================================ */

(() => {
  'use strict';

  const path = window.location.pathname;

  if (
    !path.includes('/collections/') ||
    path.includes('/products/')
  ) {
    return;
  }

  const GRID_SELECTOR = '.product-list';
  const PAGINATION_SELECTOR = '.pagination';
  const WRAPPER_CLASS = 'au-load-more-wrap';

  let reinitTimer = null;
  let internalMutation = false;


  /* ==========================================================
     LANGUAGE
     ========================================================== */

  function isFrench() {
    const lang =
      (document.documentElement.lang || '')
        .toLowerCase();

    return (
      lang.startsWith('fr') ||
      /^\/fr(?:\/|$)/i.test(
        window.location.pathname
      )
    );
  }


  function labels() {
    return isFrench()
      ? {
          button: 'VOIR PLUS',
          loading: 'CHARGEMENT',
          products: 'produits',
          of: 'sur',
          complete: 'TOUS LES PRODUITS SONT AFFICHÉS'
        }
      : {
          button: 'LOAD MORE',
          loading: 'LOADING',
          products: 'products',
          of: 'of',
          complete: 'ALL PRODUCTS SHOWN'
        };
  }


  /* ==========================================================
     PAGE / PAGINATION HELPERS
     ========================================================== */

  function getPageFromUrl(
    value = window.location.href
  ) {
    try {
      const url =
        new URL(
          value,
          window.location.origin
        );

      return parseInt(
        url.searchParams.get('page') || '1',
        10
      ) || 1;

    } catch (_) {
      return 1;
    }
  }


  function getNextUrl(
    pagination,
    currentPage
  ) {
    if (!pagination) {
      return null;
    }

    const relNext =
      pagination.querySelector(
        'a[rel="next"]'
      );

    if (relNext?.href) {
      return relNext.href;
    }

    const links =
      [
        ...pagination.querySelectorAll(
          'a[href]'
        )
      ];

    let nextUrl = null;
    let nextPage = Infinity;

    links.forEach(link => {
      try {
        const url =
          new URL(
            link.href,
            window.location.origin
          );

        const page =
          parseInt(
            url.searchParams.get('page'),
            10
          );

        if (
          Number.isFinite(page) &&
          page > currentPage &&
          page < nextPage
        ) {
          nextPage = page;
          nextUrl = link.href;
        }

      } catch (_) {}
    });

    return nextUrl;
  }


  /* ==========================================================
     PRODUCT COUNTS
     ========================================================== */

  function parseNumber(value) {
    if (!value) {
      return 0;
    }

    const cleaned =
      String(value)
        .replace(/[^\d]/g, '');

    const number =
      parseInt(
        cleaned,
        10
      );

    return Number.isFinite(number)
      ? number
      : 0;
  }


  function getTotalProducts() {
    /*
     * Stretch currently prints text such as:
     * "427 products Filter and sort (427)"
     * or the French equivalent.
     *
     * Try explicit count attributes/selectors first,
     * then fall back to the visible collection text.
     */

    const selectors = [
      '[data-product-count]',
      '[data-products-count]',
      '[data-total-products]',
      '.collection__results-count',
      '.collection-toolbar__products-count',
      '.facets__results-count',
      '.collection-toolbar [class*="count"]',
      '[class*="collection"] [class*="count"]'
    ];

    for (
      const selector
      of selectors
    ) {
      const elements =
        document.querySelectorAll(
          selector
        );

      for (
        const element
        of elements
      ) {
        const candidates = [
          element.getAttribute(
            'data-product-count'
          ),
          element.getAttribute(
            'data-products-count'
          ),
          element.getAttribute(
            'data-total-products'
          ),
          element.textContent
        ];

        for (
          const candidate
          of candidates
        ) {
          const match =
            String(candidate || '')
              .match(
                /(\d[\d\s,.]*)/
              );

          const number =
            parseNumber(
              match?.[1]
            );

          if (number > 0) {
            return number;
          }
        }
      }
    }

    const root =
      document.querySelector('main') ||
      document.body;

    const text =
      root?.innerText ||
      root?.textContent ||
      '';

    const patterns = [
      /(\d[\d\s,.]*)\s+products?\b/i,
      /(\d[\d\s,.]*)\s+produits?\b/i,
      /products?\s*\(\s*(\d[\d\s,.]*)\s*\)/i,
      /produits?\s*\(\s*(\d[\d\s,.]*)\s*\)/i
    ];

    for (
      const pattern
      of patterns
    ) {
      const match =
        text.match(pattern);

      const number =
        parseNumber(
          match?.[1]
        );

      if (number > 0) {
        return number;
      }
    }

    return 0;
  }


  function getLoadedProducts(
    grid
  ) {
    if (!grid) {
      return 0;
    }

    const cards =
      grid.querySelectorAll(
        'product-card'
      );

    if (cards.length) {
      return cards.length;
    }

    return grid.children.length;
  }


  /* ==========================================================
     SWATCH KING DYNAMIC RENDER
     ========================================================== */

  function renderDynamicSwatches() {
    /*
     * StarApps / Swatch King documents
     * vsk:render:swatches for dynamic collection content.
     *
     * Fire it AFTER the full batch exists in the live DOM.
     */
    const fire = () => {
      document.dispatchEvent(
        new CustomEvent(
          'vsk:render:swatches',
          {
            bubbles: true
          }
        )
      );
    };

    requestAnimationFrame(
      () => {
        fire();

        /*
         * Very short fallback only if the app/global is still
         * finishing its own startup. This is NOT a polling loop.
         */
        if (!window.VariantSwatchKing) {
          setTimeout(
            fire,
            120
          );
        }
      }
    );
  }


  /* ==========================================================
     UI
     ========================================================== */

  function ensureStyles() {
    if (
      document.getElementById(
        'au-load-more-style'
      )
    ) {
      return;
    }

    const style =
      document.createElement(
        'style'
      );

    style.id =
      'au-load-more-style';

    style.textContent = `
      .${WRAPPER_CLASS} {
        width: min(390px, calc(100% - 36px));
        margin: 48px auto 64px;
        text-align: center;
      }

      .au-load-more-count {
        margin: 0 0 12px;
        font-size: 10px;
        line-height: 1.25;
        font-weight: 600;
        letter-spacing: .12em;
        text-transform: uppercase;
        color: rgba(20,20,20,.72);
      }

      .au-load-more-progress {
        position: relative;
        width: 100%;
        height: 2px;
        margin: 0 0 16px;
        overflow: hidden;
        background: rgba(20,20,20,.14);
      }

      .au-load-more-progress__fill {
        display: block;
        width: 0%;
        height: 100%;
        background: #202020;
        transform-origin: left center;
        transition: width .38s ease;
      }

      .${WRAPPER_CLASS}.is-loading
      .au-load-more-progress__fill {
        animation:
          au-load-more-pulse
          .75s
          ease-in-out
          infinite alternate;
      }

      @keyframes au-load-more-pulse {
        from {
          opacity: .45;
        }

        to {
          opacity: 1;
        }
      }

      .au-load-more-button {
        appearance: none;
        -webkit-appearance: none;
        width: 100%;
        min-height: 46px;
        border: 1px solid #202020;
        border-radius: 2px;
        padding: 12px 22px;
        background: #202020;
        color: #fff;
        font: inherit;
        font-size: 11px;
        line-height: 1;
        font-weight: 600;
        letter-spacing: .12em;
        text-transform: uppercase;
        cursor: pointer;
        transition:
          background .2s ease,
          color .2s ease,
          opacity .2s ease;
      }

      .au-load-more-button:hover {
        background: #fff;
        color: #202020;
      }

      .au-load-more-button:disabled {
        cursor: wait;
        opacity: .62;
      }

      .au-load-more-button[hidden] {
        display: none !important;
      }

      .au-load-more-complete {
        display: none;
        margin-top: 2px;
        font-size: 10px;
        line-height: 1.25;
        font-weight: 600;
        letter-spacing: .12em;
        text-transform: uppercase;
        color: rgba(20,20,20,.58);
      }

      .${WRAPPER_CLASS}.is-complete
      .au-load-more-complete {
        display: block;
      }

      @media (max-width: 699px) {
        .${WRAPPER_CLASS} {
          width: calc(100% - 28px);
          margin-top: 36px;
          margin-bottom: 48px;
        }

        .au-load-more-button {
          min-height: 44px;
        }
      }
    `;

    document.head.appendChild(
      style
    );
  }


  function createUI(
    pagination
  ) {
    document
      .querySelectorAll(
        `.${WRAPPER_CLASS}`
      )
      .forEach(
        element =>
          element.remove()
      );

    const copy =
      labels();

    const wrapper =
      document.createElement(
        'div'
      );

    wrapper.className =
      WRAPPER_CLASS;

    wrapper.innerHTML = `
      <div
        class="au-load-more-count"
        aria-live="polite"
      ></div>

      <div
        class="au-load-more-progress"
        aria-hidden="true"
      >
        <span
          class="au-load-more-progress__fill"
        ></span>
      </div>

      <button
        type="button"
        class="au-load-more-button"
      >
        ${copy.button}
      </button>

      <div
        class="au-load-more-complete"
      >
        ${copy.complete}
      </div>
    `;

    pagination.parentNode.insertBefore(
      wrapper,
      pagination
    );

    return wrapper;
  }


  /* ==========================================================
     MAIN
     ========================================================== */

  function initLoadMore() {
    const grid =
      document.querySelector(
        GRID_SELECTOR
      );

    const pagination =
      document.querySelector(
        PAGINATION_SELECTOR
      );

    if (
      !grid ||
      !pagination
    ) {
      return;
    }

    if (
      grid.dataset.auLoadMoreInitialized ===
      'true'
    ) {
      return;
    }

    grid.dataset.auLoadMoreInitialized =
      'true';

    ensureStyles();

    const wrapper =
      createUI(
        pagination
      );

    const countElement =
      wrapper.querySelector(
        '.au-load-more-count'
      );

    const progressFill =
      wrapper.querySelector(
        '.au-load-more-progress__fill'
      );

    const button =
      wrapper.querySelector(
        '.au-load-more-button'
      );

    const copy =
      labels();

    pagination.style.display =
      'none';

    let currentPage =
      getPageFromUrl();

    let nextUrl =
      getNextUrl(
        pagination,
        currentPage
      );

    let totalProducts =
      getTotalProducts();

    let loading =
      false;


    function updateUI() {
      const loaded =
        getLoadedProducts(
          grid
        );

      /*
       * Re-read total after a Stretch filter refresh,
       * because the count can change with active filters.
       */
      const currentTotal =
        getTotalProducts();

      if (currentTotal > 0) {
        totalProducts =
          currentTotal;
      }

      if (
        totalProducts > 0
      ) {
        const visible =
          Math.min(
            loaded,
            totalProducts
          );

        countElement.textContent =
          `${visible} ${copy.of} ` +
          `${totalProducts} ${copy.products}`;

        const percent =
          Math.max(
            0,
            Math.min(
              100,
              (
                visible /
                totalProducts
              ) * 100
            )
          );

        progressFill.style.width =
          `${percent}%`;

      } else {
        countElement.textContent =
          `${loaded} ${copy.products}`;

        /*
         * Unknown exact total:
         * keep a subtle partial progress state
         * until the final page is reached.
         */
        progressFill.style.width =
          nextUrl
            ? '35%'
            : '100%';
      }

      if (!nextUrl) {
        wrapper.classList.add(
          'is-complete'
        );

        button.hidden =
          true;

        progressFill.style.width =
          '100%';

      } else {
        wrapper.classList.remove(
          'is-complete'
        );

        button.hidden =
          false;
      }

      wrapper.dataset.paginationSnapshot =
        pagination.innerHTML;
    }


    async function loadNextPage() {
      if (
        loading ||
        !nextUrl
      ) {
        return;
      }

      loading =
        true;

      button.disabled =
        true;

      button.textContent =
        copy.loading;

      wrapper.classList.add(
        'is-loading'
      );

      const requestedUrl =
        nextUrl;

      const requestedPage =
        getPageFromUrl(
          requestedUrl
        );

      try {
        const response =
          await fetch(
            requestedUrl,
            {
              credentials:
                'same-origin',

              headers: {
                'X-Requested-With':
                  'XMLHttpRequest'
              }
            }
          );

        if (!response.ok) {
          throw new Error(
            'Unable to load page ' +
            requestedPage
          );
        }

        const html =
          await response.text();

        const doc =
          new DOMParser()
            .parseFromString(
              html,
              'text/html'
            );

        const nextGrid =
          doc.querySelector(
            GRID_SELECTOR
          );

        const nextPagination =
          doc.querySelector(
            PAGINATION_SELECTOR
          );

        if (!nextGrid) {
          throw new Error(
            'Product grid not found'
          );
        }

        /*
         * IMPORTANT PERFORMANCE FIX:
         * Build the WHOLE next page off-DOM and append it once.
         *
         * The previous infinite-scroll code appended each product
         * separately, causing repeated MutationObserver/app work.
         */
        const fragment =
          document.createDocumentFragment();

        [
          ...nextGrid.children
        ].forEach(
          child => {
            fragment.appendChild(
              document.importNode(
                child,
                true
              )
            );
          }
        );

        internalMutation =
          true;

        grid.appendChild(
          fragment
        );

        currentPage =
          requestedPage;

        if (nextPagination) {
          pagination.innerHTML =
            nextPagination.innerHTML;

          nextUrl =
            getNextUrl(
              pagination,
              currentPage
            );

        } else {
          nextUrl = null;
        }

        /*
         * Tell Swatch King exactly once that dynamic
         * collection cards were added.
         */
        renderDynamicSwatches();

        /*
         * Let the DOM/apps receive the new batch before
         * updating the progress UI and releasing the button.
         */
        await new Promise(
          resolve =>
            requestAnimationFrame(
              () =>
                requestAnimationFrame(
                  resolve
                )
            )
        );

        updateUI();

      } catch (error) {
        console.error(
          '[AU NOIR Load More]',
          error
        );

        /*
         * Keep native pagination available as a safe fallback
         * if the AJAX request ever fails.
         */
        pagination.style.display =
          '';

      } finally {
        loading =
          false;

        button.disabled =
          false;

        button.textContent =
          copy.button;

        wrapper.classList.remove(
          'is-loading'
        );

        setTimeout(
          () => {
            internalMutation =
              false;
          },
          150
        );
      }
    }


    button.addEventListener(
      'click',
      loadNextPage
    );

    updateUI();
  }


  /* ==========================================================
     INITIAL START
     ========================================================== */

  function start() {
    initLoadMore();
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      {
        once: true
      }
    );

  } else {
    start();
  }


  /* ==========================================================
     STRETCH FILTER / SORT SUPPORT
     ========================================================== */

  const mutationObserver =
    new MutationObserver(
      mutations => {
        if (internalMutation) {
          return;
        }

        let relevant =
          false;

        for (
          const mutation
          of mutations
        ) {
          if (
            mutation.type ===
              'childList' &&
            (
              mutation.addedNodes.length ||
              mutation.removedNodes.length
            )
          ) {
            relevant =
              true;
            break;
          }
        }

        if (!relevant) {
          return;
        }

        clearTimeout(
          reinitTimer
        );

        reinitTimer =
          setTimeout(
            () => {
              const grid =
                document.querySelector(
                  GRID_SELECTOR
                );

              const pagination =
                document.querySelector(
                  PAGINATION_SELECTOR
                );

              if (
                !grid ||
                !pagination
              ) {
                return;
              }

              const wrapper =
                document.querySelector(
                  `.${WRAPPER_CLASS}`
                );

              /*
               * New Stretch grid:
               * initialize normally.
               */
              if (
                grid.dataset
                  .auLoadMoreInitialized !==
                'true'
              ) {
                initLoadMore();
                return;
              }

              /*
               * Same grid but Stretch refreshed filtering/sorting:
               * pagination changed independently of our Load More.
               */
              if (
                wrapper &&
                wrapper.dataset
                  .paginationSnapshot !==
                  pagination.innerHTML
              ) {
                delete grid.dataset
                  .auLoadMoreInitialized;

                wrapper.remove();

                initLoadMore();
              }
            },
            80
          );
      }
    );


  function startObserver() {
    const root =
      document.querySelector('main') ||
      document.body;

    if (!root) {
      return;
    }

    mutationObserver.observe(
      root,
      {
        childList: true,
        subtree: true
      }
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      {
        once: true
      }
    );

  } else {
    startObserver();
  }

})();


/* ============================================================
   AU NOIR
   FORCE IN-STOCK ON ALL STRETCH COLLECTION AJAX REQUESTS
   ============================================================

   No redirect.
   No second page load.
   No click simulation.
   Does not replace Stretch filtering.

   It simply changes Stretch's SAME request from:

   /collections/blazers?filter.v.option.size=56

   to:

   /collections/blazers?filter.v.option.size=56
   &filter.v.availability=1
   ============================================================ */

(() => {
  'use strict';

  // PERFORMANCE/SAFETY: never patch fetch/XHR outside collection pages.
  const path = window.location.pathname;
  if (!path.includes('/collections/') || path.includes('/products/')) return;

  const STOCK_PARAM = 'filter.v.availability';
  const STOCK_VALUE = '1';


  /*
   * Determines whether the request is for a collection page.
   *
   * Includes:
   * /collections/blazers
   * /fr/collections/blazers
   * /collections/blazers?page=2
   *
   * Excludes product URLs such as:
   * /collections/blazers/products/product-name
   */
  function isCollectionRequest(url) {
    return (
      url.pathname.includes('/collections/') &&
      !url.pathname.includes('/products/')
    );
  }


  /*
   * Add availability=1 to a collection URL.
   */
  function addInStockParameter(value) {
    try {

      const originalValue = value;
      const url = new URL(value, window.location.origin);

      if (!isCollectionRequest(url)) {
        return originalValue;
      }

      url.searchParams.set(
        STOCK_PARAM,
        STOCK_VALUE
      );


      /*
       * If Stretch gave fetch() a relative URL,
       * return another relative URL.
       */
      if (
        typeof originalValue === 'string' &&
        !originalValue.startsWith('http://') &&
        !originalValue.startsWith('https://')
      ) {
        return (
          url.pathname +
          url.search +
          url.hash
        );
      }


      return url.toString();

    } catch (error) {

      return value;

    }
  }



  /* ==========================================================
     PATCH FETCH()

     Stretch uses AJAX to refresh collection products/filtering.
     We modify the URL BEFORE the same request is sent.
     ========================================================== */

  if (window.fetch) {

    const originalFetch = window.fetch;

    window.fetch = function(input, options) {

      try {

        let method = 'GET';

        if (options && options.method) {
          method = options.method;
        } else if (input instanceof Request) {
          method = input.method;
        }


        if (
          String(method).toUpperCase() === 'GET'
        ) {

          /*
           * fetch(new Request(...))
           */
          if (input instanceof Request) {

            const modifiedURL =
              addInStockParameter(input.url);


            if (modifiedURL !== input.url) {

              input = new Request(
                modifiedURL,
                input
              );

            }

          }


          /*
           * fetch(new URL(...))
           */
          else if (input instanceof URL) {

            const modifiedURL =
              addInStockParameter(
                input.toString()
              );

            input = new URL(
              modifiedURL,
              window.location.origin
            );

          }


          /*
           * fetch('/collections/...')
           */
          else if (typeof input === 'string') {

            input =
              addInStockParameter(input);

          }

        }

      } catch (error) {
        /*
         * If anything unexpected happens,
         * Stretch's normal fetch still continues.
         */
      }


      return originalFetch.call(
        this,
        input,
        options
      );

    };

  }



  /* ==========================================================
     PATCH XMLHttpRequest AS FALLBACK

     If any Stretch component/app uses XHR instead of fetch,
     apply the exact same stock condition.
     ========================================================== */

  if (window.XMLHttpRequest) {

    const originalOpen =
      XMLHttpRequest.prototype.open;


    XMLHttpRequest.prototype.open =
      function(
        method,
        url,
        async,
        user,
        password
      ) {

        try {

          if (
            String(method).toUpperCase() === 'GET'
          ) {

            url =
              addInStockParameter(url);

          }

        } catch (error) {
          /*
           * Continue with original request.
           */
        }


        return originalOpen.call(
          this,
          method,
          url,
          async,
          user,
          password
        );

      };

  }

})();

/* ============================================================
   AU NOIR — SV VARIANTS / STRETCH QUICK VIEW
   V6 — STABLE SWATCH MAP / ONE-TAP RETURN TO ORIGINAL

   CORE FIX
   - Learn a stable swatch-position -> product URL map.
   - The CURRENT/original StarApps swatch is often the one item with no
     swatch-url. We explicitly map that missing/selected item to the product
     that was visible when Quick View opened.
   - Fresh product markup can hydrate later without changing what a tap means.
   - Only ONE press event is captured on modern mobile (pointerdown). We do
     not let touchstart overwrite the URL captured by pointerdown.
   - Every click is owned by this Quick View module; latest tap wins.
   ============================================================ */

(function () {
  'use strict';

  const SWATCH_SELECTOR = [
    '.swatch-group-selector',
    '.star-set-image',
    '.swatch-view-item'
  ].join(',');

  const CACHE = new Map();
  const STATE = new WeakMap();


  function normalizeUrl(value) {
    if (!value || typeof value !== 'string') return '';

    try {
      const url = new URL(value, window.location.origin);
      const productIndex = url.pathname.indexOf('/products/');

      if (productIndex === -1) return '';

      url.pathname = url.pathname.slice(productIndex);

      return url.pathname + url.search;
    } catch (_) {
      return '';
    }
  }


  function productPath(value) {
    const normalized = normalizeUrl(value);
    if (!normalized) return '';

    try {
      return new URL(normalized, window.location.origin).pathname;
    } catch (_) {
      return '';
    }
  }


  function sameProduct(a, b) {
    const aPath = productPath(a);
    const bPath = productPath(b);

    return Boolean(aPath && bPath && aPath === bPath);
  }


  function handleToUrl(value) {
    if (!value || typeof value !== 'string') return '';

    if (!/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(value)) {
      return '';
    }

    return `/products/${value}`;
  }


  function getState(modal) {
    let state = STATE.get(modal);

    if (!state) {
      const initialUrl = normalizeUrl(
        modal.dataset.auVisibleProductUrl ||
        modal.getAttribute('product-url') ||
        ''
      );

      state = {
        requestId: 0,
        pressedUrl: '',
        pressedIndex: -1,
        pressedAt: 0,
        initialUrl,
        originalIndex: -1,
        urlsByIndex: []
      };

      STATE.set(modal, state);
    }

    return state;
  }


  function findSwatch(target) {
    if (!target?.closest) return null;
    return target.closest(SWATCH_SELECTOR);
  }


  function getListing(root) {
    if (!root) return null;

    if (root.matches?.('variant-king-combined-listing')) {
      return root;
    }

    return root.querySelector?.('variant-king-combined-listing') || null;
  }


  /*
   * Return one stable item per linked product.
   * Prefer .swatch-group-selector because that is the StarApps element that
   * normally owns swatch-url. Fallbacks cover alternate mobile markup.
   */
  function getSwatchItems(root) {
    const listing = getListing(root);
    if (!listing) return [];

    let items = [
      ...listing.querySelectorAll('.swatch-group-selector')
    ];

    if (items.length) return items;

    items = [
      ...listing.querySelectorAll('.swatch-view-item')
    ];

    if (items.length) return items;

    return [
      ...listing.querySelectorAll('.star-set-image')
    ];
  }


  function canonicalSwatch(swatch, modal) {
    if (!swatch || !modal) return null;

    const listing = swatch.closest('variant-king-combined-listing');
    if (!listing || !modal.contains(listing)) return null;

    return (
      swatch.closest('.swatch-group-selector') ||
      swatch.closest('.swatch-view-item') ||
      swatch.closest('.star-set-image') ||
      swatch
    );
  }


  /*
   * IMPORTANT: direct-only lookup.
   * Do NOT walk upward into the Variant King container because a parent can
   * expose the CURRENT product URL and make an original-color tap resolve to
   * the product that is already visible.
   */
  function getDirectSwatchUrl(item) {
    if (!item) return '';

    const directCandidates = [
      item.getAttribute?.('swatch-url'),
      item.getAttribute?.('data-product-url'),
      item.getAttribute?.('data-url'),
      item.getAttribute?.('href')
    ];

    for (const candidate of directCandidates) {
      const url = normalizeUrl(candidate || '');
      if (url) return url;
    }

    const handleCandidates = [
      item.getAttribute?.('data-product-handle'),
      item.getAttribute?.('data-handle')
    ];

    for (const handle of handleCandidates) {
      const url = handleToUrl(handle || '');
      if (url) return url;
    }

    const nested = item.querySelector?.(
      '[swatch-url], [data-product-url], [data-url], a[href*="/products/"]'
    );

    if (nested) {
      const nestedCandidates = [
        nested.getAttribute('swatch-url'),
        nested.getAttribute('data-product-url'),
        nested.getAttribute('data-url'),
        nested.getAttribute('href')
      ];

      for (const candidate of nestedCandidates) {
        const url = normalizeUrl(candidate || '');
        if (url) return url;
      }
    }

    return '';
  }


  function looksSelected(item) {
    if (!item) return false;

    const selectors = [
      '[aria-selected="true"]',
      '[aria-current="true"]',
      '[data-selected="true"]',
      '[data-active="true"]',
      '.selected',
      '.is-selected',
      '.active',
      '.is-active',
      '.current',
      '.is-current',
      '.checked',
      '.is-checked'
    ].join(',');

    if (item.matches?.(selectors)) return true;
    return Boolean(item.querySelector?.(selectors));
  }


  /*
   * Learn URLs by stable swatch position.
   * StarApps commonly omits swatch-url on the CURRENT product. If exactly
   * one item is missing a URL, that missing item is the current product.
   */
  function learnSwatchMap(modal, root, currentUrl) {
    const state = getState(modal);
    const items = getSwatchItems(root);

    if (!items.length) return;

    const normalizedCurrent = normalizeUrl(currentUrl || '');
    const found = items.map(getDirectSwatchUrl);

    found.forEach((url, index) => {
      if (url) {
        state.urlsByIndex[index] = url;
      }
    });

    if (!normalizedCurrent) return;

    let currentIndex = found.findIndex(
      url => url && sameProduct(url, normalizedCurrent)
    );

    if (currentIndex === -1) {
      currentIndex = items.findIndex(looksSelected);
    }

    if (currentIndex === -1) {
      const missing = [];

      found.forEach((url, index) => {
        if (!url) missing.push(index);
      });

      if (missing.length === 1) {
        currentIndex = missing[0];
      }
    }

    if (currentIndex !== -1) {
      // Use the actual current URL, including ?variant when supplied.
      state.urlsByIndex[currentIndex] = normalizedCurrent;

      if (
        state.originalIndex === -1 &&
        state.initialUrl &&
        sameProduct(normalizedCurrent, state.initialUrl)
      ) {
        state.originalIndex = currentIndex;
      }
    }
  }


  function getClickedInfo(swatch, modal) {
    const item = canonicalSwatch(swatch, modal);
    const listing = item?.closest('variant-king-combined-listing');

    if (!item || !listing) {
      return { index: -1, url: '' };
    }

    const items = getSwatchItems(listing);
    const index = items.indexOf(item);

    const state = getState(modal);

    let url =
      (index >= 0 ? state.urlsByIndex[index] : '') ||
      getDirectSwatchUrl(item);

    if (
      !url &&
      index >= 0 &&
      index === state.originalIndex
    ) {
      url = state.initialUrl;
    }

    return {
      index,
      url: normalizeUrl(url)
    };
  }


  function fetchProduct(url) {
    const normalized = normalizeUrl(url);

    if (!normalized) {
      return Promise.reject(new Error('Invalid product URL'));
    }

    if (CACHE.has(normalized)) {
      return CACHE.get(normalized);
    }

    const request = fetch(normalized, {
      credentials: 'same-origin'
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }

        return response.text();
      })
      .catch(error => {
        CACHE.delete(normalized);
        throw error;
      });

    CACHE.set(normalized, request);
    return request;
  }


  async function buildQuickBuy(url) {
    const html = await fetchProduct(url);

    const doc = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const template = doc.getElementById('quick-buy-content');

    if (!template) {
      throw new Error('#quick-buy-content missing');
    }

    const content = template.content
      .cloneNode(true)
      .firstElementChild;

    if (!content) {
      throw new Error('Quick Buy empty');
    }

    return content;
  }


  async function preloadGallery(gallery) {
    if (!gallery) return;

    const images = [...gallery.querySelectorAll('img')].slice(0, 3);

    const jobs = images.map(img => {
      img.loading = 'eager';

      return new Promise(resolve => {
        if (img.complete && img.naturalWidth > 0) {
          resolve();
          return;
        }

        let done = false;

        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };

        img.addEventListener('load', finish, { once: true });
        img.addEventListener('error', finish, { once: true });

        const src = img.currentSrc || img.src;

        if (src) {
          const preload = new Image();
          preload.onload = finish;
          preload.onerror = finish;
          preload.src = src;
        }
      });
    });

    await Promise.race([
      Promise.all(jobs),
      new Promise(resolve => setTimeout(resolve, 500))
    ]);
  }


  async function switchProduct(modal, targetUrl) {
    targetUrl = normalizeUrl(targetUrl);
    if (!modal || !targetUrl) return;

    const state = getState(modal);
    const requestId = ++state.requestId;

    try {
      const currentQuickBuy = modal.querySelector('.quick-buy');

      const currentGallery = currentQuickBuy?.querySelector(
        ':scope > product-gallery.quick-buy__gallery'
      );

      const currentInfo = currentQuickBuy?.querySelector(
        ':scope > .v-stack.gap-6'
      );

      if (!currentQuickBuy || !currentGallery || !currentInfo) {
        throw new Error('Current Quick View structure missing');
      }

      const freshContent = await buildQuickBuy(targetUrl);

      if (state.requestId !== requestId) return;

      const freshQuickBuy = freshContent.matches?.('.quick-buy')
        ? freshContent
        : freshContent.querySelector('.quick-buy');

      if (!freshQuickBuy) {
        throw new Error('.quick-buy missing');
      }

      const freshGallery = freshQuickBuy.querySelector(
        ':scope > product-gallery.quick-buy__gallery'
      );

      const freshInfo = freshQuickBuy.querySelector(
        ':scope > .v-stack.gap-6'
      );

      if (!freshGallery || !freshInfo) {
        throw new Error('Fresh Quick View structure missing');
      }

      /*
       * Learn the fresh product's map BEFORE inserting it. Its own current
       * swatch may be the single item without swatch-url, so targetUrl tells
       * us exactly which index that missing item represents.
       */
      learnSwatchMap(modal, freshInfo, targetUrl);

      await preloadGallery(freshGallery);

      if (state.requestId !== requestId) return;

      const oldHeight = currentQuickBuy.getBoundingClientRect().height;

      if (oldHeight) {
        currentQuickBuy.style.minHeight = oldHeight + 'px';
      }

      if (state.requestId !== requestId) return;

      currentGallery.replaceWith(freshGallery);
      currentInfo.replaceWith(freshInfo);

      modal.setAttribute('product-url', targetUrl);
      modal.dataset.auVisibleProductUrl = targetUrl;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          currentQuickBuy.style.removeProperty('min-height');
        });
      });

      modal.dispatchEvent(
        new CustomEvent('sv:quick-view-change', {
          bubbles: true,
          detail: { productUrl: targetUrl }
        })
      );

    } catch (error) {
      if (state.requestId === requestId) {
        console.error('[AU NOIR Quick View V6]', error);
      }
    }
  }


  function prepareModalMap(modal) {
    if (!modal) return;

    const state = getState(modal);

    const currentUrl = normalizeUrl(
      modal.dataset.auVisibleProductUrl ||
      state.initialUrl ||
      modal.getAttribute('product-url') ||
      ''
    );

    learnSwatchMap(modal, modal, currentUrl);
  }


  /*
   * Capture exactly once per physical tap.
   * Modern iOS/Android support Pointer Events, so touchstart is ONLY a
   * fallback for old browsers. This prevents the same tap from overwriting
   * its own pressedUrl with a second event.
   */
  function captureAndIsolate(event) {
    const swatch = findSwatch(event.target);
    if (!swatch) return;

    const modal = swatch.closest('quick-buy-modal[open]');
    if (!modal) return;

    prepareModalMap(modal);

    const state = getState(modal);
    const info = getClickedInfo(swatch, modal);

    state.pressedIndex = info.index;
    state.pressedUrl = info.url;
    state.pressedAt = performance.now();

    if (info.url) {
      fetchProduct(info.url).catch(() => {});
    }

    event.stopPropagation();
    event.stopImmediatePropagation();
  }


  if ('PointerEvent' in window) {
    document.addEventListener(
      'pointerdown',
      captureAndIsolate,
      { capture: true, passive: true }
    );
  } else {
    document.addEventListener(
      'touchstart',
      captureAndIsolate,
      { capture: true, passive: true }
    );
  }


  /* Desktop/keyboard warm-up only. */
  function warmFromEvent(event) {
    const swatch = findSwatch(event.target);
    if (!swatch) return;

    const modal = swatch.closest('quick-buy-modal[open]');
    if (!modal) return;

    prepareModalMap(modal);

    const info = getClickedInfo(swatch, modal);
    if (!info.url) return;

    fetchProduct(info.url).catch(() => {});
  }

  document.addEventListener('pointerover', warmFromEvent, true);
  document.addEventListener('focusin', warmFromEvent, true);


  document.addEventListener(
    'click',
    function (event) {
      const swatch = findSwatch(event.target);
      if (!swatch) return;

      const modal = swatch.closest('quick-buy-modal[open]');
      if (!modal) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      prepareModalMap(modal);

      const state = getState(modal);
      const now = performance.now();

      let url = '';

      if (
        state.pressedUrl &&
        now - state.pressedAt < 2200
      ) {
        url = state.pressedUrl;
      }

      if (!url) {
        const info = getClickedInfo(swatch, modal);
        url = info.url;
      }

      /*
       * Absolute original-product fallback. If StarApps has not hydrated the
       * original swatch yet but we know this is its stable position, use the
       * URL captured when the Quick View first opened.
       */
      if (
        !url &&
        state.pressedIndex >= 0 &&
        state.pressedIndex === state.originalIndex
      ) {
        url = state.initialUrl;
      }

      state.pressedUrl = '';
      state.pressedIndex = -1;
      state.pressedAt = 0;

      if (!url) {
        console.warn(
          '[AU NOIR Quick View V6] No mapped product URL for swatch',
          swatch,
          state.urlsByIndex
        );
        return;
      }

      switchProduct(modal, url);
    },
    true
  );


  /*
   * Learn the initial map as soon as Stretch reports the dialog visible.
   * This is faster and more reliable than waiting for the first color tap.
   */
  document.addEventListener(
    'dialog:after-show',
    event => {
      const modal =
        event.target?.matches?.('quick-buy-modal')
          ? event.target
          : document.querySelector('quick-buy-modal[open]');

      if (!modal) return;

      requestAnimationFrame(() => {
        prepareModalMap(modal);
      });
    }
  );

})();

/* ==========================================================
   PROGUS STORE LOCATOR - MOBILE GEOLOCATION FIX
   PERFORMANCE OPTIMIZED
   ========================================================== */

(function () {
  'use strict';

  const FRAME_SELECTOR =
    'iframe[src*="proguscommerce.com"], iframe[src*="progus.com"]';

  function fixFrame(frame) {
    if (!frame || !frame.matches?.(FRAME_SELECTOR)) return;

    const currentAllow = frame.getAttribute('allow') || '';

    if (!currentAllow.toLowerCase().includes('geolocation')) {
      frame.setAttribute(
        'allow',
        currentAllow
          ? currentAllow + '; geolocation'
          : 'geolocation'
      );

      if (!frame.dataset.progusGeoReloaded) {
        frame.dataset.progusGeoReloaded = 'true';

        const src = frame.getAttribute('src');
        if (src) frame.setAttribute('src', src);
      }
    }
  }

  function scan(root) {
    if (!root) return;

    if (root.nodeType === Node.ELEMENT_NODE &&
        root.matches?.(FRAME_SELECTOR)) {
      fixFrame(root);
    }

    root.querySelectorAll?.(FRAME_SELECTOR).forEach(fixFrame);
  }

  scan(document);

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          scan(node);
        }
      });
    }
  });

  const root = document.querySelector('main') || document.documentElement;
  if (root) {
    observer.observe(root, {
      childList: true,
      subtree: true
    });
  }

  window.addEventListener('load', () => scan(document), { once: true });
})();

/* SA Variants - high quality + garment/right-side crop — OPTIMIZED */
(function () {
  'use strict';

  function improveSwatch(swatch) {
    if (!swatch || swatch.dataset.hqSwatch === 'true') return;

    const bg = swatch.style.backgroundImage;
    if (!bg) return;

    const match = bg.match(/url\(["']?(.*?)["']?\)/);
    if (!match || !match[1]) return;

    try {
      const url = new URL(match[1], window.location.origin);

      url.searchParams.set('width', '200');
      url.searchParams.set('height', '200');
      url.searchParams.set('crop', 'right');

      swatch.style.backgroundImage = 'url("' + url.toString() + '")';
      swatch.dataset.hqSwatch = 'true';
    } catch (_) {}
  }

  function improveSASwatches(root = document) {
    if (root.nodeType === Node.ELEMENT_NODE &&
        root.matches?.('.star-set-image')) {
      improveSwatch(root);
    }

    root.querySelectorAll?.('.star-set-image').forEach(improveSwatch);
  }

  improveSASwatches(document);

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          improveSASwatches(node);
        }
      });
    }
  });

  const root = document.querySelector('main') || document.body;
  if (root) {
    observer.observe(root, {
      childList: true,
      subtree: true
    });
  }
})();

/* ============================================================
   AU NOIR
   SYNC QUICK SIZE ADD WITH SV / STARAPPS LINKED PRODUCT SWATCHES
   PERFORMANCE OPTIMIZED

   IMPORTANT:
   - Does NOT fetch every collection product on page load.
   - Does NOT watch the full document for URL mutations.
   - Fetches only after a real customer changes a linked color.
   - Product JSON is cached, so repeated cards/colors reuse the request.
   ============================================================ */

(() => {
  'use strict';

  if (window.AUNoirQuickSizeSyncInstalled) return;
  window.AUNoirQuickSizeSyncInstalled = true;

  const ROOT = window.Shopify?.routes?.root || '/';
  const PRODUCT_CACHE = new Map();


  const getHandleFromUrl = (url) => {
    if (!url) return null;

    try {
      const parsed = new URL(url, window.location.origin);
      const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
      return match ? match[1] : null;
    } catch (_) {
      return null;
    }
  };


  const getCurrentHandle = (card) => {
    const titleLink = card.querySelector(
      '.product-title[href*="/products/"]'
    );

    if (titleLink) {
      const handle = getHandleFromUrl(titleLink.href);
      if (handle) return handle;
    }

    const imageLink = card.querySelector(
      '.product-card__media[href*="/products/"]'
    );

    if (imageLink) {
      const handle = getHandleFromUrl(imageLink.href);
      if (handle) return handle;
    }

    return card.getAttribute('handle');
  };


  const getSizeOption = (product) => {
    if (!Array.isArray(product.options)) return null;

    let sizeOption = product.options.find(option => {
      const name = typeof option === 'string'
        ? option
        : option.name;

      if (!name) return false;

      const cleanName = name.toLowerCase().trim();

      return (
        cleanName === 'size' ||
        cleanName === 'taille' ||
        cleanName === 'grandeur' ||
        cleanName.includes('size') ||
        cleanName.includes('taille')
      );
    });

    if (!sizeOption) {
      const candidates = product.options
        .map((option, index) => {
          if (typeof option === 'string') {
            const values = [
              ...new Set(
                product.variants.map(
                  variant => variant.options?.[index]
                )
              )
            ].filter(Boolean);

            return {
              name: option,
              position: index + 1,
              values
            };
          }

          return option;
        })
        .sort(
          (a, b) =>
            (b.values?.length || 0) -
            (a.values?.length || 0)
        );

      sizeOption = candidates[0];
    }

    if (!sizeOption) return null;

    if (typeof sizeOption === 'string') {
      return {
        name: sizeOption,
        position: product.options.indexOf(sizeOption) + 1
      };
    }

    return {
      name: sizeOption.name,
      position: sizeOption.position
    };
  };


  function fetchProductJSON(handle) {
    if (!handle) return Promise.resolve(null);

    if (PRODUCT_CACHE.has(handle)) {
      return PRODUCT_CACHE.get(handle);
    }

    const request = fetch(
      `${ROOT}products/${encodeURIComponent(handle)}.js`,
      { credentials: 'same-origin' }
    )
      .then(response => {
        if (!response.ok) return null;
        return response.json();
      })
      .catch(error => {
        PRODUCT_CACHE.delete(handle);
        console.error('AU NOIR Quick Size fetch error:', error);
        return null;
      });

    PRODUCT_CACHE.set(handle, request);
    return request;
  }


  const updateQuickSizes = async (card, forcedHandle = null) => {
    if (!card) return;

    const sizeContainer =
      card.querySelector('.boss-quick-size__sizes') ||
      card.querySelector('.boss-quick-shop__sizes');

    if (!sizeContainer) return;

    const handle = forcedHandle || getCurrentHandle(card);
    if (!handle) return;

    if (sizeContainer.dataset.productHandle === handle) {
      return;
    }

    try {
      const product = await fetchProductJSON(handle);
      if (!product) return;

      // The card may have been switched again while the request was running.
      const selectedFromCard = card.dataset.auQuickSizeRequestedHandle;
      if (selectedFromCard && selectedFromCard !== handle) {
        return;
      }

      const sizeOption = getSizeOption(product);
      if (!sizeOption) return;

      const sizeIndex = Number(sizeOption.position) - 1;

      const existingProductForm =
        sizeContainer.querySelector('product-form');

      if (!existingProductForm) {
        return;
      }

      const template = existingProductForm.cloneNode(true);
      const fragment = document.createDocumentFragment();
      const usedSizes = new Set();

      product.variants.forEach(variant => {
        const size =
          variant.options?.[sizeIndex] ||
          variant[`option${sizeOption.position}`];

        if (!size || usedSizes.has(size)) return;
        usedSizes.add(size);

        const productForm = template.cloneNode(true);
        const form = productForm.querySelector('form');
        if (!form) return;

        const idInput = form.querySelector('input[name="id"]');
        if (!idInput) return;
        idInput.value = variant.id;

        const quantityInput = form.querySelector(
          'input[name="quantity"]'
        );
        if (quantityInput) quantityInput.value = 1;

        const button = form.querySelector(
          '.boss-quick-size__button, ' +
          '.boss-quick-shop__size, ' +
          'button[type="submit"]'
        );

        if (!button) return;

        const buttonContent = button.querySelector('.button__content');

        if (buttonContent) {
          buttonContent.textContent = size;
          buttonContent.removeAttribute('rotated-text');
          buttonContent.removeAttribute('up-text');
          buttonContent.removeAttribute('glowing');
        } else {
          button.textContent = size;
        }

        button.disabled = !variant.available;
        button.setAttribute(
          'aria-disabled',
          variant.available ? 'false' : 'true'
        );
        button.classList.toggle('is-disabled', !variant.available);

        fragment.appendChild(productForm);
      });

      // Replace once instead of repeatedly mutating the live DOM.
      sizeContainer.replaceChildren(fragment);
      sizeContainer.dataset.productHandle = handle;
      card.dataset.quickSizeHandle = handle;

    } catch (error) {
      console.error('AU NOIR Quick Size sync error:', error);
    }
  };


  document.addEventListener(
    'click',
    event => {
      if (!event.isTrusted) return;

      /* Quick View is handled separately. Never let collection-card
         Quick Size synchronization react to swatches inside the modal. */
      if (event.target.closest?.('quick-buy-modal')) return;

      const card = event.target.closest?.('product-card');
      if (!card) return;

      // Prefer the actual linked-product element because it gives us
      // the new product immediately; no need to watch href mutations.
      const linkedSwatch = event.target.closest?.(
        '.swatch-group-selector[swatch-url]'
      );

      const anySASwatch = linkedSwatch || event.target.closest?.(
        '.star-set-image, .swatch-view-item'
      );

      if (!anySASwatch) return;

      const directHandle = getHandleFromUrl(
        linkedSwatch?.getAttribute('swatch-url') || ''
      );

      if (directHandle) {
        card.dataset.auQuickSizeRequestedHandle = directHandle;

        // First pass can update immediately while the app changes images/title.
        updateQuickSizes(card, directHandle);

        // Second pass is cache-backed and handles an SA card re-render.
        setTimeout(
          () => updateQuickSizes(card, directHandle),
          260
        );

        return;
      }

      // Fallback for StarApps markup that does not expose swatch-url
      // on the clicked element. This runs only after a real click.
      setTimeout(() => {
        const handle = getCurrentHandle(card);
        if (!handle) return;
        card.dataset.auQuickSizeRequestedHandle = handle;
        updateQuickSizes(card, handle);
      }, 180);
    },
    true
  );

})();

/* ============================================================
   AU NOIR — REMOVE STRAY COLON BEFORE PRICE

   Handles the initial product page plus dynamically inserted /
   replaced prices from Stretch, Quick Buy, SV Variants, filters,
   infinite scroll, and AJAX updates.

   Example fixed:
   <span class="sr-only">Selling price</span>: €94.95
   becomes:
   <span class="sr-only">Selling price</span>€94.95

   IMPORTANT:
   - Only removes a leading colon in the text node immediately after
     the sr-only accessibility label.
   - Does NOT edit currency symbols or price digits.
   ============================================================ */

(() => {
  'use strict';

  const PRICE_SELECTOR =
    'sale-price, compare-at-price';


  function cleanPriceElement(
    priceElement
  ) {

    if (!priceElement) {
      return;
    }


    const srOnly =
      priceElement.querySelector(
        ':scope > .sr-only'
      );


    if (!srOnly) {
      return;
    }


    let node =
      srOnly.nextSibling;


    while (
      node &&
      node.nodeType === Node.TEXT_NODE &&
      node.textContent.trim() === ''
    ) {
      node = node.nextSibling;
    }


    if (
      !node ||
      node.nodeType !== Node.TEXT_NODE
    ) {
      return;
    }


    const current =
      node.textContent;


    const cleaned =
      current.replace(
        /^\s*:\s*/,
        ''
      );


    if (cleaned !== current) {
      node.textContent = cleaned;
    }

  }


  function cleanPrices(
    root = document
  ) {

    if (
      root.nodeType === Node.ELEMENT_NODE &&
      root.matches?.(PRICE_SELECTOR)
    ) {
      cleanPriceElement(root);
    }


    root
      .querySelectorAll?.(PRICE_SELECTOR)
      .forEach(cleanPriceElement);

  }


  function initialClean() {
    cleanPrices(document);
  }


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialClean,
      { once: true }
    );

  } else {

    initialClean();

  }


  const observer =
    new MutationObserver(
      mutations => {

        for (const mutation of mutations) {

          if (
            mutation.type === 'characterData'
          ) {

            const priceElement =
              mutation.target.parentElement
                ?.closest?.(PRICE_SELECTOR);


            if (priceElement) {
              cleanPriceElement(
                priceElement
              );
            }


            continue;
          }


          if (
            mutation.type === 'childList'
          ) {

            mutation.addedNodes.forEach(
              node => {

                if (
                  node.nodeType ===
                  Node.ELEMENT_NODE
                ) {

                  cleanPrices(node);

                }

              }
            );


            const targetPrice =
              mutation.target.nodeType ===
              Node.ELEMENT_NODE
                ? mutation.target.closest?.(
                    PRICE_SELECTOR
                  )
                : mutation.target.parentElement
                    ?.closest?.(
                      PRICE_SELECTOR
                    );


            if (targetPrice) {
              cleanPriceElement(
                targetPrice
              );
            }

          }

        }

      }
    );


  const startObserver = () => {

    if (!document.documentElement) {
      return;
    }


    observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true,
        characterData: true
      }
    );

  };


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      { once: true }
    );

  } else {

    startObserver();

  }

})();

/* ============================================================
   AU NOIR — FIRST COLLECTION PRODUCT MOBILE SWIPE HINT
   SMOOTH / AFTER FULL PAGE LOAD

   - Mobile only
   - Collection pages only
   - First collection product only
   - Waits until page + first 2 images are fully loaded
   ============================================================ */

(() => {
  'use strict';

  const MOBILE_QUERY = '(max-width: 699px)';

  if (!window.matchMedia(MOBILE_QUERY).matches) return;

  if (
    !window.location.pathname.includes('/collections/') ||
    window.location.pathname.includes('/products/')
  ) {
    return;
  }

  if (window.auNoirFirstProductSwipeHint) return;
  window.auNoirFirstProductSwipeHint = true;


  /* ============================================================
     SMOOTH SCROLL ANIMATION
     ============================================================ */

  function animateScroll(
    gallery,
    from,
    to,
    duration,
    callback
  ) {

    const startTime = performance.now();

    function ease(t) {

      /*
        Smooth ease-in-out
      */

      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    function frame(now) {

      const progress = Math.min(
        (now - startTime) / duration,
        1
      );

      const eased = ease(progress);

      gallery.scrollLeft =
        from +
        ((to - from) * eased);

      if (progress < 1) {

        requestAnimationFrame(frame);

      } else {

        gallery.scrollLeft = to;

        if (callback) callback();

      }

    }

    requestAnimationFrame(frame);
  }


  /* ============================================================
     WAIT FOR IMAGE
     ============================================================ */

  async function waitForImage(img) {

    if (!img) return;

    /*
      Force first product's second image to load.
      Only affects this one product.
    */

    img.loading = 'eager';

    if (!img.complete) {

      await new Promise(resolve => {

        const finish = () => resolve();

        img.addEventListener(
          'load',
          finish,
          { once: true }
        );

        img.addEventListener(
          'error',
          finish,
          { once: true }
        );

      });

    }

    /*
      Wait until browser has actually decoded
      the image before starting animation.
    */

    if (typeof img.decode === 'function') {

      try {

        await img.decode();

      } catch (_) {}

    }

  }


  /* ============================================================
     RUN SWIPE HINT
     ============================================================ */

  async function runHint(gallery) {

    if (!gallery) return;

    if (
      gallery.dataset.auSwipeHintDone === 'true'
    ) {
      return;
    }

    const images =
      gallery.querySelectorAll(
        '.product-card__gallery-image'
      );

    if (images.length < 2) return;


    /* ----------------------------------------------------------
       WAIT FOR IMAGE #1 + IMAGE #2
       ---------------------------------------------------------- */

    await Promise.all([
      waitForImage(images[0]),
      waitForImage(images[1])
    ]);


    /*
      Give browser two complete paint cycles
      after decoding.
    */

    await new Promise(resolve => {

      requestAnimationFrame(() => {

        requestAnimationFrame(resolve);

      });

    });


    if (
      gallery.clientWidth < 50 ||
      gallery.scrollWidth <=
        gallery.clientWidth + 20
    ) {
      return;
    }


    gallery.dataset.auSwipeHintDone = 'true';


    /* ==========================================================
       TEMPORARILY DISABLE SNAP
       ========================================================== */

    const oldSnap =
      gallery.style.getPropertyValue(
        'scroll-snap-type'
      );

    const oldSnapPriority =
      gallery.style.getPropertyPriority(
        'scroll-snap-type'
      );

    const oldBehavior =
      gallery.style.getPropertyValue(
        'scroll-behavior'
      );

    const oldBehaviorPriority =
      gallery.style.getPropertyPriority(
        'scroll-behavior'
      );


    gallery.style.setProperty(
      'scroll-snap-type',
      'none',
      'important'
    );

    gallery.style.setProperty(
      'scroll-behavior',
      'auto',
      'important'
    );


    gallery.scrollLeft = 0;


    /* ==========================================================
       DISTANCE
       ========================================================== */

    const distance = Math.min(
      75,
      Math.max(
        55,
        gallery.clientWidth * 0.18
      )
    );


    /*
      Small delay after everything is completely ready.
    */

    await new Promise(
      resolve =>
        setTimeout(resolve, 500)
    );


    /* ==========================================================
       MOVE TOWARD IMAGE 2
       ========================================================== */

    animateScroll(
      gallery,
      0,
      distance,
      500,

      () => {

        /*
          Let customer see the second photo slightly.
        */

        setTimeout(() => {


          /* ====================================================
             RETURN TO IMAGE 1
             ==================================================== */

          animateScroll(
            gallery,
            distance,
            0,
            600,

            () => {

              gallery.scrollLeft = 0;


              /* ================================================
                 RESTORE NORMAL CAROUSEL
                 ================================================ */

              if (oldSnap) {

                gallery.style.setProperty(
                  'scroll-snap-type',
                  oldSnap,
                  oldSnapPriority
                );

              } else {

                gallery.style.removeProperty(
                  'scroll-snap-type'
                );

              }


              if (oldBehavior) {

                gallery.style.setProperty(
                  'scroll-behavior',
                  oldBehavior,
                  oldBehaviorPriority
                );

              } else {

                gallery.style.removeProperty(
                  'scroll-behavior'
                );

              }

            }
          );

        }, 250);

      }
    );

  }


  /* ============================================================
     FIND FIRST REAL COLLECTION PRODUCT
     ============================================================ */

  function initialize() {

    const productList =
      document.querySelector(
        'main .product-list'
      ) ||
      document.querySelector(
        '.product-list'
      );

    if (!productList) return;


    const firstCard =
      productList.querySelector(
        'product-card'
      );

    if (!firstCard) return;


    const gallery =
      firstCard.querySelector(
        '.product-card__media--gallery'
      );

    if (!gallery) return;


    /* ==========================================================
       WAIT UNTIL PRODUCT IS ACTUALLY VISIBLE
       ========================================================== */

    const observer =
      new IntersectionObserver(
        entries => {

          const entry = entries[0];

          if (!entry.isIntersecting) return;

          observer.disconnect();

          runHint(gallery);

        },
        {
          threshold: 0.4
        }
      );


    observer.observe(gallery);

  }


  /* ============================================================
     WAIT UNTIL EVERYTHING ON PAGE IS LOADED
     ============================================================ */

  function startAfterFullLoad() {

    /*
      Extra delay gives Shopify / apps /
      swatches / layout time to settle.
    */

    setTimeout(() => {

      requestAnimationFrame(() => {

        requestAnimationFrame(() => {

          initialize();

        });

      });

    }, 700);

  }


  if (document.readyState === 'complete') {

    startAfterFullLoad();

  } else {

    window.addEventListener(
      'load',
      startAfterFullLoad,
      { once: true }
    );

  }

})();

/* =========================================================
   AU NOIR — MOBILE FILTER GLASS BUTTON
   PERFORMANCE OPTIMIZED / SINGLE COPY
   ========================================================= */

(function () {
  'use strict';

  const MOBILE_QUERY = '(max-width: 699px)';
  const path = window.location.pathname;

  if (!window.matchMedia(MOBILE_QUERY).matches) return;
  if (!path.includes('/collections/') || path.includes('/products/')) return;


  function considerElement(el) {
    if (!el || !el.matches?.('button, a, [role="button"], summary')) {
      return;
    }

    if (el.classList.contains('au-mobile-filter-glass')) return;

    const text = (el.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    const isFilterButton =
      text.includes('filter and sort') ||
      text.includes('filtrer et trier');

    if (!isFilterButton) return;

    // Only now do a layout read. We no longer measure every button/link.
    const rect = el.getBoundingClientRect();

    if (rect.width > 180 && rect.height < 100) {
      el.classList.add('au-mobile-filter-glass');
      el.parentElement?.classList.add(
        'au-mobile-filter-glass-wrapper'
      );
    }
  }


  function styleWithin(root) {
    if (!root) return;

    if (root.nodeType === Node.ELEMENT_NODE) {
      considerElement(root);
    }

    root.querySelectorAll?.(
      'button, a, [role="button"], summary'
    ).forEach(considerElement);
  }


  function initialStyle() {
    styleWithin(document.querySelector('main') || document);
  }


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialStyle,
      { once: true }
    );
  } else {
    initialStyle();
  }


  const startObserver = () => {
    const root = document.querySelector('main');
    if (!root) return;

    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            styleWithin(node);
          }
        });
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true
    });
  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      { once: true }
    );
  } else {
    startObserver();
  }

})();

/* =========================================================
   AU NOIR — GLASS DRAWERS + QUICK VIEW
   Stretch Theme / Shadow DOM
   Mobile + Desktop — PERFORMANCE OPTIMIZED
   ========================================================= */

(function () {
  'use strict';

  const SHADOW_STYLE_ID = 'au-noir-glass-drawer-shadow';

  const DRAWER_SELECTOR = [
    'x-drawer',
    'cart-drawer',
    'search-drawer',
    'facet-drawer',
    'facets-drawer',
    'header-drawer',
    'quick-buy-modal',
    'quick-view-modal',
    '[id*="quick-buy"]',
    '[id*="quick-view"]',
    '[class*="quick-buy-modal"]',
    '[class*="quick-view-modal"]',
    '[id*="drawer"]',
    '[class~="drawer"]'
  ].join(',');

  const shadowCSS = `
    :host {
      --au-glass-bg: rgba(18, 18, 20, 0.74);
    }

    header,
    footer,
    .drawer__header,
    .drawer__footer,
    .dialog__header,
    .dialog__footer,
    .modal__header,
    .modal__footer,
    [part="header"],
    [part="footer"],
    [class*="drawer-header"],
    [class*="drawer-footer"],
    [class*="drawer__header"],
    [class*="drawer__footer"],
    [class*="modal-header"],
    [class*="modal-footer"],
    [class*="modal__header"],
    [class*="modal__footer"] {
      background: transparent !important;
      background-color: transparent !important;
      box-shadow: none !important;
    }

    header,
    .drawer__header,
    .dialog__header,
    .modal__header,
    [part="header"] {
      border-bottom: 1px solid rgba(255,255,255,0.09) !important;
    }

    footer,
    .drawer__footer,
    .dialog__footer,
    .modal__footer,
    [part="footer"] {
      border-top: 1px solid rgba(255,255,255,0.09) !important;
    }

    [part="content"],
    .drawer__content,
    .dialog__content,
    .modal__content,
    .quick-buy-modal__content,
    .quick-view-modal__content {
      background: rgba(18,18,20,0.74) !important;
      background-color: rgba(18,18,20,0.74) !important;
      -webkit-backdrop-filter: blur(28px) saturate(180%) !important;
      backdrop-filter: blur(28px) saturate(180%) !important;
    }

    .quick-buy-modal__inner,
    .quick-buy-modal__content,
    .quick-buy-modal__product,
    .quick-view-modal__inner,
    .quick-view-modal__content,
    .quick-view-modal__product,
    .product-info,
    .product-info__block-list,
    .product-info__block-item,
    .product-info__buy-buttons,
    .product,
    .product__info,
    .product__info-container {
      background: transparent !important;
      background-color: transparent !important;
    }

    .product-gallery,
    .product-gallery__media,
    .product-gallery__media-list,
    .product-gallery__carousel,
    .product-media-container {
      background: transparent !important;
      background-color: transparent !important;
    }

    button[aria-label*="Close"],
    button[aria-label*="close"],
    button[aria-label*="Fermer"],
    button[aria-label*="fermer"],
    .drawer__close,
    .dialog__close,
    .modal__close,
    [class*="close-button"] {
      background: rgba(255,255,255,0.08) !important;
      border: 1px solid rgba(255,255,255,0.10) !important;
      border-radius: 999px !important;
      -webkit-backdrop-filter: blur(14px) !important;
      backdrop-filter: blur(14px) !important;
    }
  `;


  function injectIntoShadowRoot(element) {
    if (!element?.shadowRoot) return false;

    if (element.shadowRoot.querySelector('#' + SHADOW_STYLE_ID)) {
      return true;
    }

    const style = document.createElement('style');
    style.id = SHADOW_STYLE_ID;
    style.textContent = shadowCSS;
    element.shadowRoot.appendChild(style);
    return true;
  }


  function scanRoot(root) {
    if (!root) return false;

    let found = false;

    if (root.nodeType === Node.ELEMENT_NODE &&
        root.matches?.(DRAWER_SELECTOR)) {
      found = true;
      injectIntoShadowRoot(root);
    }

    const descendants = root.querySelectorAll?.(DRAWER_SELECTOR);

    if (descendants?.length) {
      found = true;
      descendants.forEach(injectIntoShadowRoot);
    }

    return found;
  }


  function retryTarget(root) {
    // Do not create retry timers for ordinary product/image/text insertions.
    // Retry only when the inserted subtree actually contains a drawer/modal.
    const found = scanRoot(root);
    if (!found) return;

    // Custom elements may attach their shadowRoot just after insertion.
    setTimeout(() => scanRoot(root), 60);
    setTimeout(() => scanRoot(root), 250);
  }


  function initialScan() {
    scanRoot(document);
  }


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialScan,
      { once: true }
    );
  } else {
    initialScan();
  }


  // One small post-load pass for Stretch components upgraded late.
  window.addEventListener(
    'load',
    () => setTimeout(initialScan, 250),
    { once: true }
  );


  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          retryTarget(node);
        }
      });
    }
  });


  const startObserver = () => {
    const root = document.body || document.documentElement;
    if (!root) return;

    observer.observe(root, {
      childList: true,
      subtree: true
    });
  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startObserver,
      { once: true }
    );
  } else {
    startObserver();
  }


  document.addEventListener(
    'dialog:after-show',
    event => retryTarget(event.target || document),
    true
  );


  document.addEventListener(
    'click',
    event => {
      const trigger = event.target.closest?.(
        '[aria-controls*="quick-buy"],' +
        '[aria-controls*="quick-view"],' +
        '.product-card__quick-add-button'
      );

      if (!trigger) return;

      const id = trigger.getAttribute('aria-controls');
      const target = id ? document.getElementById(id) : null;

      if (target) {
        retryTarget(target);
      }
    },
    true
  );

})();
